// Live guidance: given a generated course and a position fix, find the active lane and the signed
// cross-track error (which way + how far to steer). All math in projected meters; the steer direction
// uses the A→B travel direction so the sign is stable regardless of lane vertex ordering.

import { lineString, nearestPointOnLine, lineSliceAlong } from '@turf/turf';
import { projectorForLon, meanLon } from './projection';
import {
  sub,
  normalize,
  nearestOnPolyline,
  signedOffset,
  normalizeDeg,
  type Vec2,
} from './geometry';
import type { Course, Guidance, Lane, LonLat, SteerDirection } from './types';

export interface GuidanceInput {
  lat: number;
  lon: number;
  /** Optional true heading, degrees, for heading-error reporting. */
  heading?: number;
}

export interface GuidanceOptions {
  /** Below this absolute cross-track (meters), report `steer: 'on'`. Default 5 cm. */
  onThresholdM?: number;
}

/** Compute guidance for a fix against a course, or null if the course has no usable lanes. */
export function computeGuidance(
  course: Course,
  fix: GuidanceInput,
  opts: GuidanceOptions = {},
): Guidance | null {
  if (course.lanes.length === 0) return null;

  const proj = projectorForLon(meanLon([course.ab.a, course.ab.b]));
  const P = proj.toMeters([fix.lon, fix.lat]);
  const A = proj.toMeters(course.ab.a);
  const B = proj.toMeters(course.ab.b);
  const u = normalize(sub(B, A)); // canonical travel direction (same as swath generation)

  let best: { index: number; dist: number; nearest: Vec2 } | null = null;
  for (const lane of course.lanes) {
    if (lane.coordinates.length < 2) continue;
    const ptsM = lane.coordinates.map((c) => proj.toMeters(c));
    const r = nearestOnPolyline(P, ptsM);
    if (!best || r.dist < best.dist) best = { index: lane.index, dist: r.dist, nearest: r.point };
  }
  if (!best) return null;

  const crossTrackM = signedOffset(P, best.nearest, u); // + = right of lane, − = left
  const onThreshold = opts.onThresholdM ?? 0.05;
  let steer: SteerDirection;
  if (Math.abs(crossTrackM) <= onThreshold) steer = 'on';
  else steer = crossTrackM > 0 ? 'left' : 'right'; // right of the lane → steer left to correct

  const out: Guidance = {
    activeLaneIndex: best.index,
    crossTrackM,
    steer,
    nearestPoint: proj.toLonLat(best.nearest),
  };
  if (fix.heading !== undefined)
    out.headingErrorDeg = normalizeDeg(fix.heading - course.headingDeg);
  return out;
}

/**
 * The next `metersAhead` of the active lane in front of the vehicle — for highlighting just the
 * upcoming stretch rather than the whole row. Returns [] if there's no usable active lane.
 */
export function activeAhead(course: Course, fix: GuidanceInput, metersAhead = 50): LonLat[] {
  const g = computeGuidance(course, fix);
  if (!g) return [];
  const segs = course.lanes.filter(
    (l) => l.index === g.activeLaneIndex && l.coordinates.length >= 2,
  );
  if (segs.length === 0) return [];

  const pt: LonLat = [fix.lon, fix.lat];
  let bestSeg: Lane = segs[0]!;
  let bestDist = Infinity;
  let startKm = 0;
  for (const seg of segs) {
    const snap = nearestPointOnLine(lineString(seg.coordinates), pt, { units: 'kilometers' });
    const d = (snap.properties.dist as number | undefined) ?? Infinity;
    if (d < bestDist) {
      bestDist = d;
      bestSeg = seg;
      startKm = (snap.properties.location as number | undefined) ?? 0;
    }
  }
  const slice = lineSliceAlong(
    lineString(bestSeg.coordinates),
    startKm,
    startKm + metersAhead / 1000,
    { units: 'kilometers' },
  );
  return slice.geometry.coordinates as LonLat[];
}
